import {
  from,
  ApolloLink,
  ApolloClient,
  InMemoryCache,
} from '@apollo/client'
import { createHttpLink } from '@apollo/client/link/http'
import { RetryLink } from '@apollo/client/link/retry'
import { setContext } from '@apollo/client/link/context'
import { get as getConfig } from 'config'
import _get from 'lodash/get'
import { WEB_CLIENT_ATTRIBUTES } from 'constants/client-attributes'

const config = getConfig()
const graphqlServer = _get(config, ['servers', 'graphql'])

export const APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST = 'cache-first'
export const APOLLO_CLIENT_FETCH_POLICY_CACHE_ONLY = 'cache-only'
export const APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK = 'cache-and-network'
export const APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY = 'network-only'
export const APOLLO_CLIENT_FETCH_POLICY_NO_CACHE = 'no-cache'
export const APOLLO_CLIENT_FETCH_POLICY_STANDBY = 'standby'
export const APOLLO_CLIENT_ERROR_POLICY_ALL = 'all'
export const APOLLO_CLIENT_ERROR_POLICY_NONE = 'none'
export const APOLLO_CLIENT_ERROR_POLICY_IGNORE = 'ignore'

/**
 * The Apollo Client Links
 * @param {Object} options - The options
 * @param {String} options.authJwt - The user's JWT
 * @returns {ApolloLink} - The Apollo Link
 */
export function getApolloClientLinks () {
  const graphqlUri = `${String(graphqlServer).replace(/\/$/, '')}/graphql`

  // Create HTTP Link with multipart HTTP support for subscriptions
  const httpLink = createHttpLink({
    uri: graphqlUri,
    // Enable multipart HTTP for subscriptions
    useGETForQueries: false,
  })

  // Context link to add multipart headers for subscriptions
  const multipartLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        // Add Accept header for multipart HTTP subscriptions
        Accept: 'multipart/mixed;subscriptionSpec="1.0", application/json',
      },
    }
  })

  const authLink = new ApolloLink((operation, forward) => {
    // all queries/mutations will need to send in the auth jwt via context if auth is required
    const { jwt } = operation.getContext()

    operation.setContext(({ headers }) => ({
      headers: {
        ...headers,
        // Set the clientAttributes for web app (same as REST requests)
        'X-Client-Attributes': WEB_CLIENT_ATTRIBUTES,
        ...(jwt ? { authorization: `Bearer ${jwt}` } : {}),
      },
    }))

    return forward(operation)
  })

  // Simple retry link with minimal configuration
  const retryLink = new RetryLink({
    delay: {
      initial: 300,
      max: 3000,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: (error) => {
        const status = error?.status || error?.statusCode
        return status >= 500 || status === 429
      },
    },
  })

  const linkChain = from([
    multipartLink,
    authLink,
    retryLink,
    httpLink,
  ])

  return linkChain
}

// Apollo Client
export const client = new ApolloClient({
  link: getApolloClientLinks(),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          playlistItemList: {
            keyArgs: ['playlists', 'userCurrent'],
            merge (existing = {}, incoming) {
              return incoming.offset === 0
                ? incoming
                : {
                  ...incoming,
                  playlistItems: [
                    ...(existing.playlistItems || []),
                    ...(incoming.playlistItems || []),
                  ],
                }
            },
          },
        },
      },
      UserProfile: {
        keyFields: ['uid'],
        fields: {
          watchHistory: {
            keyArgs: ['filter'],
            merge (existing = [], incoming, { args }) {
              if (!args || !args.offset || args.offset === 0) {
                return incoming
              }
              return [...existing, ...incoming]
            },
          },
        },
      },
    },
  }),
  // connectToDevTools: true,
})
